import { useState } from "react";
import { toast } from "sonner";
import { Building2, Hammer, Send, Loader2, CheckCircle2, FileText } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/i18n/LanguageProvider";

/**
 * ProjectBulkPurchase — discreet but visible block for strategic investors.
 *
 * Two offering tracks:
 *   1. Purchase of the entire project (planning + permits + project GmbH).
 *   2. Acquisition of individual units in shell state (Rohbau) for personal finishing.
 *
 * Comes with its own lean contact form so interested parties have a channel
 * that is clearly separate from the per-apartment inquiries. Submissions land
 * in the existing /api/inquiries pipeline but are tagged with a dedicated
 * apartment_id / apartment_label so they show up distinctly in the admin.
 */
export const ProjectBulkPurchase = () => {
    const t = useT();
    const [interest, setInterest] = useState("gesamtprojekt");
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
        privacy_consent: false,
    });
    const [submitting, setSubmitting] = useState(false);

    const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const INTEREST_LABEL = {
        gesamtprojekt: t({ de: "Gesamtprojekt-Erwerb", en: "Full project acquisition" }),
        rohbau: t({ de: "Einheiten im Rohbauzustand", en: "Shell-state units" }),
        beides: t({ de: "Beides · unverbindlich", en: "Both · non-binding" }),
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.privacy_consent) {
            toast.error(t({ de: "Bitte stimmen Sie den Datenschutzhinweisen zu.", en: "Please accept the privacy notice." }));
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/inquiries", {
                ...form,
                apartment_id: `projekt-${interest}`,
                apartment_label: `Projektanfrage · ${INTEREST_LABEL[interest]}`,
                usage_type: "kapitalanlage",
                financing: "unentschieden",
                move_in_timing: "",
            });
            toast.success(
                t({ de: "Anfrage gesendet", en: "Request sent" }),
                {
                    description: t({
                        de: "Wir melden uns persönlich und vertraulich bei Ihnen.",
                        en: "We will get back to you personally and in confidence.",
                    }),
                },
            );
            setForm({ name: "", email: "", phone: "", message: "", privacy_consent: false });
        } catch (err) {
            toast.error(
                t({ de: "Anfrage konnte nicht gesendet werden", en: "Request could not be sent" }),
                { description: formatApiErrorDetail(err.response?.data?.detail) || err.message },
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="projekt-gesamterwerb"
            className="bg-ink text-white py-24 sm:py-32"
            data-testid="project-bulk-purchase-section"
        >
            <div className="container-page">
                {/* Header — discreetly highlighted */}
                <div className="grid lg:grid-cols-12 gap-10 mb-14">
                    <div className="lg:col-span-6">
                        <span className="overline text-clay-300">
                            {t({
                                de: "Für strategische Anleger · Bauträger",
                                en: "For strategic investors · developers",
                            })}
                        </span>
                        <h2 className="h-section mt-4 text-white text-balance">
                            {t({ de: "Das gesamte Projekt oder", en: "Acquire the whole project or" })}
                            <span className="italic text-clay-300"> {t({ de: "im Rohbau übernehmen.", en: "take it on in shell state." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8">
                        <p className="body-md text-white/80">
                            {t({
                                de: (
                                    <>
                                        Neben dem Verkauf der vier Einzelwohnungen besteht zum jetzigen Zeitpunkt die Möglichkeit, das
                                        Gesamtprojekt mit der vorliegenden Planung, den erteilten baurechtlichen Genehmigungen und der
                                        Projektgesellschaft im Ganzen zu erwerben – oder einzelne Einheiten im Rohbauzustand für den
                                        eigenen, individuellen Ausbau zu vereinbaren.
                                    </>
                                ),
                                en: (
                                    <>
                                        In addition to selling the four individual apartments, the entire project is currently available
                                        for acquisition – including the existing planning, the granted building permits and the project
                                        company as a whole – or single units can be taken over in shell state for your own bespoke fit-out.
                                    </>
                                ),
                            })}
                        </p>
                        <p className="body-md text-white/55 mt-4">
                            {t({
                                de: "Diskret bearbeitet, auf Anfrage.",
                                en: "Handled in confidence, upon request.",
                            })}
                        </p>
                    </div>
                </div>

                {/* Two option cards */}
                <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-14">
                    <OptionCard
                        icon={<Building2 size={22} className="text-clay-300" />}
                        eyebrow={t({ de: "Option A", en: "Option A" })}
                        title={t({ de: "Gesamtprojekt-Erwerb", en: "Full project acquisition" })}
                        bullets={[
                            t({ de: "Aktuelle Planung (Architekt & Statik)", en: "Current planning (architect & structural)" }),
                            t({ de: "Erteilte Baugenehmigung & Bauvoranfragen", en: "Granted building permit & preliminary rulings" }),
                            t({ de: "Projektgesellschaft im Ganzen übernehmbar", en: "Project company can be taken over as a whole" }),
                            t({ de: "Optional: Mietpool-Strategie für Bestandshalter", en: "Optional: rental-pool strategy for long-term holders" }),
                        ]}
                    />
                    <OptionCard
                        icon={<Hammer size={22} className="text-clay-300" />}
                        eyebrow={t({ de: "Option B", en: "Option B" })}
                        title={t({ de: "Einheiten im Rohbauzustand", en: "Units in shell state" })}
                        bullets={[
                            t({ de: "Einzelne Wohnungen mit definiertem Bautenstand", en: "Individual apartments at a defined construction stage" }),
                            t({ de: "Eigener, individueller Innenausbau", en: "Your own bespoke interior fit-out" }),
                            t({ de: "KfW-Zuschuss / AfA weiterhin nutzbar", en: "KfW grant / depreciation still applicable" }),
                            t({ de: "Klare Abgrenzung von Gewerken & Gewährleistung", en: "Clear scope-of-work and warranty delineation" }),
                        ]}
                    />
                </div>

                {/* Contact form */}
                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <span className="overline text-clay-300">
                            {t({ de: "Vertraulich anfragen", en: "Confidential inquiry" })}
                        </span>
                        <h3 className="font-heading text-3xl sm:text-4xl text-white mt-3 text-balance">
                            {t({
                                de: "Interesse? Wir sprechen gern persönlich.",
                                en: "Interested? Let's talk personally.",
                            })}
                        </h3>
                        <p className="body-md text-white/70 mt-4">
                            {t({
                                de: "Ihre Anfrage landet direkt bei der Projektleitung und wird vertraulich behandelt. Kein Massenverteiler, kein Exposé-Bombardement.",
                                en: "Your inquiry goes straight to project management and is handled in confidence. No mass mailing.",
                            })}
                        </p>
                        <div className="mt-8 flex items-start gap-3 p-4 bg-clay-300/10 border border-clay-300/30">
                            <FileText size={16} className="text-clay-300 shrink-0 mt-0.5" />
                            <p className="font-body text-xs text-white/75 leading-relaxed">
                                {t({
                                    de: "Für eine belastbare Preisindikation erhalten Sie nach Eingang Ihrer Anfrage auf Wunsch ein Datenraum-Paket (Planung, Genehmigung, Kalkulation).",
                                    en: "Upon request, you will receive a data-room package (planning, permit, cost calculation) after your inquiry.",
                                })}
                            </p>
                        </div>
                    </div>

                    <form
                        onSubmit={submit}
                        className="lg:col-span-7 bg-white text-ink p-8 lg:p-10 space-y-6"
                        data-testid="project-inquiry-form"
                    >
                        <div>
                            <label className="label-line">
                                {t({ de: "Mein Interesse gilt", en: "My interest is in" })}
                            </label>
                            <div className="mt-2 grid sm:grid-cols-3 gap-2" role="radiogroup">
                                {Object.entries(INTEREST_LABEL).map(([key, label]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => setInterest(key)}
                                        className={`px-4 py-3 text-left text-sm border transition-colors ${
                                            interest === key
                                                ? "border-brand-500 bg-brand-500 text-white"
                                                : "border-stone-200 bg-white text-ink hover:border-brand-500"
                                        }`}
                                        data-testid={`project-interest-${key}`}
                                        aria-pressed={interest === key}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-6">
                            <Field label={`${t({ de: "Name", en: "Name" })} *`}>
                                <input
                                    type="text"
                                    required
                                    value={form.name}
                                    onChange={(e) => update("name", e.target.value)}
                                    className="input-line"
                                    placeholder={t({ de: "Vor- und Nachname", en: "First and last name" })}
                                    data-testid="project-inquiry-name"
                                />
                            </Field>
                            <Field label={`${t({ de: "E-Mail", en: "Email" })} *`}>
                                <input
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => update("email", e.target.value)}
                                    className="input-line"
                                    placeholder="name@firma.de"
                                    data-testid="project-inquiry-email"
                                />
                            </Field>
                        </div>

                        <Field label={t({ de: "Telefon (optional)", en: "Phone (optional)" })}>
                            <input
                                type="tel"
                                value={form.phone}
                                onChange={(e) => update("phone", e.target.value)}
                                className="input-line"
                                placeholder="+49 …"
                                data-testid="project-inquiry-phone"
                            />
                        </Field>

                        <Field label={t({ de: "Nachricht", en: "Message" })}>
                            <textarea
                                rows={4}
                                value={form.message}
                                onChange={(e) => update("message", e.target.value)}
                                className="input-line resize-none"
                                placeholder={t({
                                    de: "Kurze Einordnung Ihrer Absicht, Zeitschiene, Eigenkapital …",
                                    en: "Briefly describe your intent, timeline, equity …",
                                })}
                                data-testid="project-inquiry-message"
                            />
                        </Field>

                        <label className="flex items-start gap-3 cursor-pointer group">
                            <input
                                type="checkbox"
                                checked={form.privacy_consent}
                                onChange={(e) => update("privacy_consent", e.target.checked)}
                                className="mt-1 h-4 w-4 rounded-none border-stone-300 text-brand-500 focus:ring-brand-500"
                                required
                                data-testid="project-inquiry-consent"
                            />
                            <span className="font-body text-xs text-stone-600 leading-relaxed">
                                {t({
                                    de: (
                                        <>
                                            Ich willige ein, dass meine Angaben zur Bearbeitung meiner Anfrage gespeichert und vom Bauträger
                                            genutzt werden. Weitere Informationen in der{" "}
                                            <a href="/datenschutz" className="underline hover:text-brand-600">Datenschutzerklärung</a>.
                                        </>
                                    ),
                                    en: (
                                        <>
                                            I consent that my data is stored and used by the developer to process my inquiry. More info in our{" "}
                                            <a href="/datenschutz" className="underline hover:text-brand-600">privacy policy</a>.
                                        </>
                                    ),
                                })}
                            </span>
                        </label>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="project-inquiry-submit"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    {t({ de: "Wird gesendet…", en: "Sending…" })}
                                </>
                            ) : (
                                <>
                                    <Send size={16} />
                                    {t({ de: "Vertrauliche Anfrage senden", en: "Send confidential inquiry" })}
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
};

/* ------------------------------------------------------------------- */

const OptionCard = ({ icon, eyebrow, title, bullets }) => (
    <div className="bg-ink p-8 lg:p-10">
        <div className="flex items-center gap-3">
            {icon}
            <span className="overline text-clay-300">{eyebrow}</span>
        </div>
        <h3 className="font-heading text-2xl sm:text-3xl text-white mt-4">{title}</h3>
        <ul className="mt-6 space-y-3">
            {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/80">
                    <CheckCircle2 size={16} className="text-clay-300 shrink-0 mt-0.5" />
                    <span>{b}</span>
                </li>
            ))}
        </ul>
    </div>
);

const Field = ({ label, children }) => (
    <div>
        <label className="label-line">{label}</label>
        {children}
    </div>
);
